export const CUSTOM_PORT_TYPES_KEY = 'netprobuilder-custom-port-types';

export interface CustomPortType {
  id: string;
  name: string;
  icon: string;
  color: string;
  createdAt: number;
}

export const BUILT_IN_PORT_TYPE_LABELS: Record<string, string> = {
  rj45: 'RJ45',
  sfp: 'SFP',
  sfpPlus: 'SFP+',
  ac: 'AC',
  dc: 'DC',
  pvPlus: 'PV+',
  pvMinus: 'PV−',
  batPlus: 'BAT+',
  batMinus: 'BAT−',
  usb: 'USB',
  console: 'Console',
  hdmi: 'HDMI',
  audio: 'Audio',
  coax: 'Coax',
  custom: 'Custom',
};

export const BUILT_IN_PORT_TYPE_COLORS: Record<string, string> = {
  rj45: '#10b981',
  sfp: '#3b82f6',
  sfpPlus: '#3b82f6',
  ac: '#ef4444',
  dc: '#f97316',
  pvPlus: '#dc2626',
  pvMinus: '#111827',
  batPlus: '#f43f5e',
  batMinus: '#1e293b',
  usb: '#8b5cf6',
  console: '#06b6d4',
  hdmi: '#ec4899',
  audio: '#14b8a6',
  coax: '#a855f7',
  custom: '#64748b',
};

const BUILT_IN_PORT_TYPE_ICONS: Record<string, string> = {
  rj45: 'EthernetPort',
  sfp: 'Cable',
  sfpPlus: 'Cable',
  ac: 'Power',
  dc: 'Zap',
  pvPlus: 'Zap',
  pvMinus: 'Zap',
  batPlus: 'Zap',
  batMinus: 'Zap',
  usb: 'Usb',
  console: 'Terminal',
  hdmi: 'Monitor',
  audio: 'Volume2',
  coax: 'Radio',
  custom: 'Box',
};

function customTypeRecord(
  kind: string,
  customTypes: Record<string, CustomPortType>
): CustomPortType | undefined {
  if (!kind.startsWith('custom:')) return undefined;
  const id = kind.slice('custom:'.length);
  if (customTypes[id]) return customTypes[id];
  if (customTypes[kind]) return customTypes[kind];
  const lower = id.toLowerCase();
  const match = Object.entries(customTypes).find(([key]) => key.toLowerCase() === lower);
  return match?.[1];
}

let customPortTypesCache: Record<string, CustomPortType> | null = null;

export function loadCustomPortTypes(): Record<string, CustomPortType> {
  if (customPortTypesCache) return customPortTypesCache;
  try {
    const stored = localStorage.getItem(CUSTOM_PORT_TYPES_KEY);
    if (!stored) {
      customPortTypesCache = {};
      return customPortTypesCache;
    }
    const parsed = JSON.parse(stored);
    customPortTypesCache = parsed && typeof parsed === 'object' ? (parsed as Record<string, CustomPortType>) : {};
    return customPortTypesCache;
  } catch {
    customPortTypesCache = {};
    return customPortTypesCache;
  }
}

export function saveCustomPortTypes(portTypes: Record<string, CustomPortType>): void {
  customPortTypesCache = portTypes;
  try {
    localStorage.setItem(CUSTOM_PORT_TYPES_KEY, JSON.stringify(portTypes));
  } catch (error) {
    console.error('Failed to save custom port types:', error);
  }
}

/** User-facing name for a layout port kind (RJ45, SFP, or the custom type name). */
export function portKindDisplayName(
  kind?: string | null,
  customTypes: Record<string, CustomPortType> = loadCustomPortTypes()
): string {
  if (!kind) return BUILT_IN_PORT_TYPE_LABELS.rj45;
  const builtIn = BUILT_IN_PORT_TYPE_LABELS[kind];
  if (builtIn) return builtIn;
  if (kind.startsWith('custom:')) {
    const named = customTypeRecord(kind, customTypes)?.name?.trim();
    if (named) return named;
    return BUILT_IN_PORT_TYPE_LABELS.custom;
  }
  return kind;
}

export function portKindColor(
  kind?: string | null,
  customTypes: Record<string, CustomPortType> = loadCustomPortTypes()
): string {
  if (!kind) return BUILT_IN_PORT_TYPE_COLORS.rj45;
  if (BUILT_IN_PORT_TYPE_COLORS[kind]) return BUILT_IN_PORT_TYPE_COLORS[kind];
  const custom = customTypeRecord(kind, customTypes);
  if (custom?.color) return custom.color;
  return BUILT_IN_PORT_TYPE_COLORS.custom;
}

/** Lucide icon name for a kind, or undefined when the editor shows label-only (RJ45 / SFP). */
export function portKindIconName(
  kind?: string | null,
  customTypes: Record<string, CustomPortType> = loadCustomPortTypes()
): string | undefined {
  if (!kind) return undefined;
  if (BUILT_IN_PORT_TYPE_ICONS[kind]) return BUILT_IN_PORT_TYPE_ICONS[kind];
  const custom = customTypeRecord(kind, customTypes);
  if (custom?.icon) return custom.icon;
  if (kind.startsWith('custom:')) return 'Box';
  return undefined;
}
